import { Link } from 'react-router-dom';
import type { Categoria, Producto, Informacion, Banner } from '../api';
import { productoDisponibleHoy, diaDeHoy } from '../api';
import { useCart } from '../contexts/CartContext';
import Reveal from '../components/Reveal';
import Carrusel from '../components/Carrusel';

interface Props {
  categorias: Categoria[];
  productos: Producto[];
  info: Informacion | null;
  promos: Banner[];
}

const DIA_LABEL: Record<string, string> = {
  domingo: 'Domingo', lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles',
  jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado',
};

export default function Inicio({ categorias, productos, info, promos }: Props) {
  const { addItem } = useCart();
  const catAlmuerzos = categorias.find(c => c.nombre === 'Almuerzos');
  // Si el dueño eligió un plato del día en el panel admin, se muestra ese.
  // Si lo eligió pero está agotado, se muestra el mensaje genérico (no otro plato).
  // Si no ha elegido ninguno, se mantiene el comportamiento automático por día.
  const platoElegido = productos.find(p => p.plato_del_dia);
  const almuerzoHoy = platoElegido
    ? (platoElegido.disponible ? platoElegido : undefined)
    : productos.find(p => p.categoria_id === catAlmuerzos?.id && productoDisponibleHoy(p));

  const tieneGps = info?.gps_lat != null && info?.gps_lng != null;
  const googleMapsUrl = tieneGps
    ? `https://www.google.com/maps/dir/?api=1&destination=${info!.gps_lat},${info!.gps_lng}`
    : null;
  const wazeUrl = tieneGps
    ? `https://waze.com/ul?ll=${info!.gps_lat}%2C${info!.gps_lng}&navigate=yes`
    : null;
  const mapaEmbedUrl = tieneGps
    ? `https://www.google.com/maps?q=${info!.gps_lat},${info!.gps_lng}&z=16&output=embed`
    : null;

  return (
    <div>
      {/* HERO */}
      <section
        className="min-h-screen flex items-center pt-28 relative overflow-hidden"
        style={{
          background: 'radial-gradient(circle at 15% 20%, rgba(201,61,127,0.25), transparent 45%), radial-gradient(circle at 85% 75%, rgba(228,165,58,0.12), transparent 50%), linear-gradient(150deg, #150012 0%, #200020 35%, #2B0822 65%, #6D1B54 120%)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center relative z-10 w-full">
          <div className="text-center md:text-left">
            <span className="inline-flex items-center gap-2 text-gold font-semibold text-xs tracking-[3px] uppercase mb-5">
              ● Costa Rica
            </span>
            <h1 className="text-white font-display font-extrabold text-4xl md:text-5xl leading-tight mb-5">
              Sabor de casa,<br /><em className="text-gold italic">servido con cariño.</em>
            </h1>
            <p className="text-white/75 text-base max-w-md mx-auto md:mx-0 mb-8 font-light">
              Desayunos, almuerzos, empanadas y comidas rápidas hechas al momento.
              La sazón de siempre, ahora también a un mensaje de distancia.
            </p>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <Link to="/menu" className="bg-gold hover:bg-gold-light text-wine-darker font-bold text-sm px-8 py-4 rounded-full shadow-lg transition-transform hover:-translate-y-1">
                Ver el menú
              </Link>
            </div>
          </div>

          <div className="flex justify-center">
            {almuerzoHoy ? (
              <div className="bg-cream w-[300px] p-8 pb-10 relative shadow-soft rotate-3 transition-transform hover:rotate-0 duration-500">
                <span className="bg-accent text-white text-[11px] font-bold tracking-wider px-3 py-1 rounded uppercase inline-block mb-4">
                  Plato del día · {DIA_LABEL[diaDeHoy()]}
                </span>
                {almuerzoHoy.imagen_url && (
                  <img
                    src={almuerzoHoy.imagen_url}
                    alt={almuerzoHoy.nombre}
                    className="w-full h-36 object-cover rounded mb-4"
                  />
                )}
                <div className="font-display text-2xl font-bold text-wine mb-1.5">{almuerzoHoy.nombre}</div>
                {almuerzoHoy.descripcion && <div className="text-xs text-ink/50 mb-4">{almuerzoHoy.descripcion}</div>}
                <div className="border-t border-dashed border-cream-dark my-4" />
                <div className="flex justify-between items-baseline mt-3">
                  <span className="text-[11px] tracking-wider text-ink/40 uppercase">Total</span>
                  <span className="font-display text-3xl font-extrabold text-wine">₡{almuerzoHoy.precio.toLocaleString()}</span>
                </div>
                <button
                  onClick={() => addItem({ id: almuerzoHoy.id, nombre: almuerzoHoy.nombre, precio: almuerzoHoy.precio })}
                  className="mt-5 w-full bg-wine text-white font-bold text-sm py-3 rounded-xl hover:bg-wine-dark transition-colors"
                >
                  Agregar al pedido
                </button>
              </div>
            ) : (
              <div className="bg-cream w-[300px] p-8 shadow-soft rotate-3 text-center text-ink/50 text-sm">
                Consultá el menú completo del día 🍽️
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Carrusel de fotos */}
      {promos.length > 0 && (
        <section className="py-20 bg-cream">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal className="text-center max-w-xl mx-auto mb-10">
              <span className="text-accent font-bold text-xs tracking-[3px] uppercase mb-3 block">Galería</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-wine-darker">
                Así se vive la Soda
              </h2>
            </Reveal>
            <Reveal delayMs={100}>
              <Carrusel banners={promos} />
            </Reveal>
          </div>
        </section>
      )}

      {/* Categorías preview */}
      <section className={`py-20 ${promos.length > 0 ? 'bg-white' : 'bg-cream'}`}>
        <div className="max-w-6xl mx-auto px-6 text-center">
          <Reveal>
            <span className="text-accent font-bold text-xs tracking-[3px] uppercase mb-3 block">Nuestro Menú</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-wine-darker mb-10">
              Lo mejor de la zona, hecho al momento
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {categorias.map((c, i) => (
              <Reveal key={c.id} delayMs={i * 60}>
                <Link to="/menu" className="block bg-white rounded-2xl p-6 shadow-card hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300">
                  <div className="text-4xl mb-2">{c.icono}</div>
                  <div className="font-display font-bold text-wine-darker">{c.nombre}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo llegar */}
      {tieneGps && (
        <section
          className="py-20"
          style={{ background: 'linear-gradient(160deg, #2B0822, #6D1B54 80%)' }}
        >
          <div className="max-w-6xl mx-auto px-6">
            <Reveal className="text-center max-w-xl mx-auto mb-12">
              <span className="text-gold font-bold text-xs tracking-[3px] uppercase mb-3 block">Visitanos</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">¿Cómo llegar?</h2>
              <p className="text-white/70 text-sm">
                {info?.direccion || 'Tocá el mapa o elegí tu app favorita para trazar la ruta'}
              </p>
            </Reveal>

            <Reveal delayMs={100}>
              <div className="bg-cream rounded-3xl overflow-hidden shadow-soft">
                <a href={googleMapsUrl!} target="_blank" rel="noreferrer" className="block group relative">
                  <iframe
                    title="Ubicación de Soda Celes"
                    src={mapaEmbedUrl!}
                    className="w-full h-[340px] md:h-[420px] border-0 pointer-events-none grayscale-[15%] group-hover:grayscale-0 transition-all duration-500"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="absolute inset-0 bg-wine-darker/0 group-hover:bg-wine-darker/10 transition-colors duration-300 flex items-end justify-center pb-6">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-gold text-wine-darker font-bold text-xs px-4 py-2 rounded-full shadow-lg">
                      Ver mapa completo
                    </span>
                  </div>
                </a>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 p-6">
                  <a
                    href={googleMapsUrl!}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-wine/10 hover:bg-wine/20 border border-wine/20 text-wine-darker font-semibold text-sm px-6 py-3 rounded-full transition-colors w-full sm:w-auto justify-center"
                  >
                    📍 Ir con Google Maps
                  </a>
                  <a
                    href={wazeUrl!}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-wine-darker font-bold text-sm px-6 py-3 rounded-full shadow-lg transition-transform hover:-translate-y-1 w-full sm:w-auto justify-center"
                  >
                    🚗 Ir con Waze
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
